import React, { useEffect, useContext } from 'react';
import axios from 'axios';
import { InterceptorPopUpContext } from '../../contexts/interceptorPopUpContext.js';
import { useNavigate } from 'react-router';
import { NAVIGATION_PATHS } from '../../static/navigationPaths.js';
import { SSO_PROVIDER_TYLER } from '../../static/keyWords.js';
import { useErrorPages } from './ErrorPages/errorPagesContext.js';
import { useUser } from '../../contexts/userContext.js';
import {
  canAttemptSilentAuthentication,
  clearSilentAuthenticationAttempt,
  startSilentAuthentication,
} from '../../services/ssoService.js';

const currentPage =
  window.location.href.substring(window.location.href.lastIndexOf('/') + 1);

let sessionExpiredRedirecting = false;
let sessionWasEstablished = false;
let sessionAbortController = new AbortController();

const AxiosInterceptor = () => {
  const { addPopUp } = useContext(InterceptorPopUpContext);
  const { setInternalError } = useErrorPages();
  const { user, setUser } = useUser();
  const navigate = useNavigate();

  const _activeCostCenters = user?.activeCostCenterIds || [];

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (request) => {
        request.signal = sessionAbortController.signal;

        if (request.method === 'get') {
          request.params = {
            payload: request.params || {},
            activeCostCenterIds: _activeCostCenters
          };
        } else {
          if (request.data instanceof FormData) {
            request.data.append(
              'activeCostCenterIds',
              JSON.stringify(_activeCostCenters)
            );
          } else {
            request.data = {
              payload: request.data,
              activeCostCenterIds: _activeCostCenters
            };
          }
        }

        return request;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        sessionExpiredRedirecting = false;
        sessionWasEstablished = true;
        if (response.config?.url === '/api/admin/me') {
          clearSilentAuthenticationAttempt();
        }
        return response;
      },

      (error) => {
        if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
          return new Promise(() => { });
        }

        if (sessionExpiredRedirecting) {
          return new Promise(() => { });
        }

        if (!error.response) {
          if (
            error.message === 'Network Error' &&
            currentPage !== 'login'
          ) {
            addPopUp('Network error.Make sure API is running');
          }

          return Promise.reject(error);
        }

        const status = error.response.status;

        if (
          status === 400 &&
          error.config.disallowGlobalBadRequestPopups !== true
        ) {
          if (error.response?.data?.errorCode === 403) {
            addPopUp('User is view only. Request denied!');
          } else {
            //addPopUp('Bad request. Please try again.');  this is removed to avoid double popups
          }
        } else if (status === 417) {
          addPopUp('Server error. Try to logout and login again!');
        } else if (status === 504) {
          setInternalError(true);
        } else if (status === 403) {
          if (
            error.response?.data?.ssoProvider === SSO_PROVIDER_TYLER ||
            error.response?.data?.ssoLogin
          ) {
            navigate(NAVIGATION_PATHS.UNAUTHORISED_ACCESS, {
              state: {
                fromPath: location.pathname,
              },
            });
          } else {
            navigate(NAVIGATION_PATHS.INVALID_URL);
          }
        } else if (status === 401) {
          const onSessionPage = [
            NAVIGATION_PATHS.SESSION_TIMEOUT,
            NAVIGATION_PATHS.LOGIN,
            NAVIGATION_PATHS.LOGOUT,
            NAVIGATION_PATHS.SESSION_ENDED
          ].some((p) => location.pathname.startsWith(p));
          const isInitialSessionProbe =
            error.config?.url === '/api/admin/me';

          if (
            !onSessionPage &&
            isInitialSessionProbe &&
            canAttemptSilentAuthentication()
          ) {
            const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            startSilentAuthentication(returnTo);
            return new Promise(() => { });
          }

          if (!onSessionPage) {
            sessionExpiredRedirecting = true;
            sessionAbortController.abort();
            sessionAbortController = new AbortController();
            localStorage.clear();
            setUser(null);
            navigate(NAVIGATION_PATHS.LOGOUT, {
              state: {
                sessionExpired: true,
                fromPath: location.pathname,
              },
            });

            // Initial page load: SessionChecker needs the rejection to fire its
            // catch and set sessionChecked=true so children render (Logout route).
            // Mid-session: sessionChecked is already true, hang silently so
            // components without try/catch don't produce uncaught errors.
            return sessionWasEstablished
              ? new Promise(() => { })
              : Promise.reject(new axios.CanceledError('Session expired'));
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [addPopUp, navigate, setInternalError]);

  return null;
};

export default AxiosInterceptor;
